import React, { useEffect, useState } from "react";
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import NavBar from '@/components/NavBar';
import { ReturnToTop } from '@/components/ReturnToTop';
import ScrollDownArrow from '@/components/ScrollDownArrow';
import { BOOKING_URL } from '@/constants/urls';
import { Heart, Sparkles, Leaf, BookOpen, MessageCircle, Users, ArrowRight, Phone, Mail, MapPin, Baby, Flame, Brain, CheckCircle, Moon, Star, Gem, Sun, Target, User, Droplets, Wand2, Coffee, Home } from 'lucide-react';
import familyImage from '@/assets/family.webp';

const FertilitySupportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = "Fertility Support with Chinese Medicine Sunshine Coast | Natural Conception";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Compassionate fertility support with Chinese Medicine in Currimundi. Personalized care for your conception journey with acupuncture, herbs, and holistic wellness.");
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Fertility Support with Chinese Medicine Sunshine Coast | Natural Conception");
    }
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Compassionate fertility support with Chinese Medicine in Currimundi. Personalized care for your conception journey with acupuncture, herbs, and holistic wellness.");
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', "https://womenandtheway.com.au/fertility-support");
    }
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', "https://womenandtheway.com.au/family.webp");
    }
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', "Fertility Support with Chinese Medicine Sunshine Coast | Natural Conception");
    }
    const schema = {
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Women and The Way - Fertility Support",
      "image": "https://womenandtheway.com.au/family.webp",
      "description": "Compassionate fertility support with Chinese Medicine in Currimundi. Personalized care for your conception journey with acupuncture, herbs, and holistic wellness.",
      "url": "https://womenandtheway.com.au/fertility-support",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "700 Nicklin Way",
        "addressLocality": "Currimundi",
        "addressRegion": "QLD",
        "addressCountry": "AU"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -26.5270,
        "longitude": 153.0890
      },
      "priceRange": "$$",
      "serviceType": "Fertility Support, Chinese Medicine, Acupuncture, Herbal Medicine, IVF Support",
      "sameAs": [
        "https://www.facebook.com/Womenandtheway",
        "https://www.instagram.com/women_and_the_way"
      ]
    };
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const handleBooking = async () => {
    setIsLoading(true);
    try {
      window.open(BOOKING_URL, '_blank');
    } catch (error) {
      console.error('Booking error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-white via-white to-amber-50">
        <NavBar />
        {/* Hero Section */}
        <section className="relative py-20 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                  Fertility Support with Chinese Medicine
                </h1>
                <p className="text-xl md:text-2xl text-sage-600 mb-6 font-medium">
                  Because every fertility journey deserves compassion, clarity, and care.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Trying to conceive can feel exciting, confusing, exhausting - or all of the above. At Women and the Way, we honour every path to parenthood and support you with personalised, evidence-informed Chinese Medicine care that nurtures your body, mind, and hormones.
                </p>
                <p className="text-lg text-sage-700 mb-8 leading-relaxed">
                  Whether you're just starting to track your cycle, have been trying for some time, or are preparing for IVF or IUI, we're here to support your next steps. Our approach complements your existing care and works alongside <a href="/ivf-support" className="text-sage-600 hover:text-sage-800 underline transition-colors">IVF support</a> and <a href="/preconception" className="text-sage-600 hover:text-sage-800 underline transition-colors">preconception care</a>.
                </p>
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
                  onClick={handleBooking}
                  disabled={isLoading}
                >
                  <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : 'Book Now'}</span>
                  <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={familyImage} 
                  alt="Fertility support and family wellness with Chinese Medicine" 
                  className="rounded-2xl shadow-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Fertility Support Matters */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6 flex items-center justify-center gap-3">
                <Heart className="w-8 h-8 text-sage-600" />
                Why Fertility Support Matters
              </h2>
            </div>
            <div className="bg-white rounded-2xl shadow-lg border-2 border-amber-200/50 p-8 md:p-12">
              <p className="text-lg text-sage-700 mb-6 leading-relaxed">
                Fertility support with Chinese Medicine is a holistic approach that addresses the root causes of conception challenges while nurturing your overall health and wellbeing. We work with your body's natural rhythms and energy systems to create optimal conditions for conception.
              </p>
              <p className="text-lg text-sage-700 leading-relaxed">
                Our approach combines ancient wisdom with modern understanding of reproductive health, addressing everything from cycle regulation and hormone balance to stress management and emotional wellbeing. We see fertility as a reflection of your overall health and vitality.
              </p>
            </div>
          </div>
        </section>

        {/* What We Support */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Support
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Acupuncture for Fertility</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Regulate cycles, support ovulation, improve uterine blood flow, and calm the nervous system</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Leaf className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Custom Herbal Medicine</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Nourish Yin/Yang, balance hormones, support egg and sperm quality, and prepare the body for conception</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Stress & Emotional Support</h3>
                    </div>
                  <p className="text-sage-700 mb-4">Nervous system regulation, emotional balance, and mental clarity throughout your fertility journey</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Cycle Tracking Education</h3>
                    </div>
                  <p className="text-sage-700 mb-4">Learn to track your cycles, understand your fertile window, and recognize TCM patterns</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Wand2 className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">IVF/IUI Preparation</h3>
                    </div>
                  <p className="text-sage-700 mb-4">Pre- and post-transfer support, stress reduction, and optimal uterine environment preparation</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageCircle className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Lifestyle Consultations</h3>
                  </div>
                  <p className="text-sage-700 mb-4">Nutrition guidance, stress management techniques, and fertility-friendly lifestyle recommendations</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What We Treat */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What We Treat
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Reproductive Health Conditions */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Reproductive Health Conditions</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Irregular or absent periods</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Luteal phase defect or short cycles</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">PMS, spotting, or pain</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Endometriosis or PCOS</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Unexplained infertility</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Recurrent miscarriage</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Fertility Journey Support */}
              <Card className="bg-white border-2 border-amber-200/50 shadow-lg rounded-2xl hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-sage-600" />
                    <h3 className="font-serif text-2xl font-semibold text-sage-800">Fertility Journey Support</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">IVF and IUI preparation</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Male fertility concerns</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Stress and emotional support</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Hormonal balance</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Egg and sperm quality</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-sage-600" />
                      <span className="text-sage-700">Uterine lining preparation</span>
                    </div>
                  </div>
                  </CardContent>
                </Card>
            </div>
          </div>
        </section>

        {/* What Makes Our Fertility Support Unique */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                What Makes Our Fertility Support Unique
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon: "🫶", title: "We honour every journey", desc: "Your path to parenthood is unique. We meet you where you are." },
                { icon: "🌿", title: "Evidence-informed care", desc: "We blend traditional wisdom with modern fertility research and protocols" },
                { icon: "📚", title: "Comprehensive education", desc: "We empower you with knowledge about your body, cycles, and fertility" },
                { icon: "🌀", title: "Holistic approach", desc: "We address physical, emotional, and spiritual aspects of fertility" }
              ].map((feature, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl mb-3">{feature.icon}</div>
                    <h3 className="font-serif text-xl font-semibold text-sage-800 mb-2">{feature.title}</h3>
                    <p className="text-sage-700">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Treatment Plan */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent mb-6">
                Recommended Treatment Plan
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Star className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Initial Assessment</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Comprehensive consultation including cycle tracking, TCM diagnosis, and personalized treatment plan</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg mb-4">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Gem className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Active Treatment</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Weekly acupuncture sessions for 3-6 months, with herbal medicine and lifestyle support</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="bg-white rounded-xl border-2 border-amber-200/50 shadow-lg">
                <AccordionTrigger className="px-6 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <Sun className="w-6 h-6 text-sage-600" />
                    <span className="font-serif text-xl font-semibold text-sage-800">Ongoing Support</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4">
                    <p className="text-sage-700 italic">👉 Maintenance sessions every 2-4 weeks, with adjustments based on your progress and needs</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
                What's Included
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Sparkles, title: "Acupuncture sessions", desc: "Fertility-focused protocols" },
                { icon: Leaf, title: "Custom herbal prescriptions", desc: "Tailored to your pattern" },
                { icon: BookOpen, title: "Cycle tracking education", desc: "BBT, CM, and TCM patterns" },
                { icon: MessageCircle, title: "Lifestyle consultations", desc: "Nutrition and stress management" },
                { icon: Heart, title: "Emotional support", desc: "Throughout your journey" },
                { icon: Wand2, title: "IVF/IUI preparation", desc: "Pre- and post-transfer support" }
              ].map((tool, index) => (
                <Card key={index} className="bg-white border-2 border-amber-200/50 shadow-sm rounded-xl hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <tool.icon className="w-8 h-8 text-sage-600 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-semibold text-sage-800 mb-2">{tool.title}</h3>
                    <p className="text-sage-700 text-sm">{tool.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 md:px-6 bg-sage-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
              Start Your Fertility Journey
            </h2>
            <p className="text-xl text-sage-700 mb-8">
              We don't promise miracles - but we do promise care that's consistent, honest, and based on experience. Our goal is to help you feel supported, seen, and aligned with your body as you take steps toward pregnancy.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <MapPin className="w-5 h-5 text-sage-600" />
                <a 
                  href="https://maps.google.com/?q=700+Nicklin+Way+Currimundi+QLD" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-sage-800 transition-colors"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Phone className="w-5 h-5 text-sage-600" />
                <a 
                  href="tel:0415537784" 
                  className="hover:text-sage-800 transition-colors"
                >
                  0415 537 784
                </a>
              </div>
              <div className="flex items-center justify-center gap-3 text-sage-700">
                <Mail className="w-5 h-5 text-sage-600" />
                <a 
                  href="mailto:womenandtheway@gmail.com" 
                  className="hover:text-sage-800 transition-colors"
                >
                  womenandtheway@gmail.com
                </a>
              </div>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto text-center break-words !whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 !flex !flex-col sm:!flex-row items-center justify-center gap-2 mx-auto min-w-0 min-h-[60px] sm:min-h-[auto]"
              onClick={handleBooking}
              disabled={isLoading}
            >
              <span className="block break-words whitespace-normal text-center">{isLoading ? 'Booking...' : '🌱 Start where you are. We\'ll walk with you from there.'}</span>
              <ArrowRight className="w-5 h-5 ml-2 flex-shrink-0" />
            </Button>
          </div>
        </section>

        <ScrollDownArrow />
        <ReturnToTop />
      </div>
    </>
  );
};

export default FertilitySupportPage; 